import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/es";
import "dayjs/locale/pt";
import "dayjs/locale/en-gb";
import Decimal from "decimal.js";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from '@mui/icons-material/Inventory';
import EditPage from "./EditPage";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import {
  fetchForInput,
  fetchProducts,
  validateDecimal,
  formatCurrency,
  getDescription,
  getSubtotal,
  getFirstPrincipalOwner,
  validateNonEmpty,
  handleError,
  fetchOwner,
  resolveDateFormattingLocale,
  bcp47ToDayjsLocale,
  getDayjsDateTimeFormatForLocale,
  getDayjsDateFormatForLocale,
  resolveHour12Preference,
} from "../../utils/lib";
import { useTranslation } from 'react-i18next';
import useSellStore from "../../store/sellStore";
import { useAppBar } from '../../providers/AppBarProvider';
import ListItemWAvatarRemove from "../../layouts/ListItemWAvatarRemove";
import PetsIcon from '@mui/icons-material/Pets';
import ListItemProductRemove from "../../layouts/ListItemProductRemove";
import ListItemSupplyRemove from "../../layouts/ListItemSupplyRemove";
import { useSnackbar } from "../../providers/SnackBarProvider";
import ProductPriceSelector from "../../layouts/ProductPriceSelector";
import ProductUnitSelector from "../../layouts/ProductUnitSelector";
import useNavigationHelperStore from "../../store/navigationHelperStore";
import { useConfig } from "../../providers/ConfigProvider";

const EditSell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const snackbar = useSnackbar();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [deposits, setDeposits] = useState([]);
  const [sellPoints, setSellPoints] = useState([]);
  const [paymentsTypes, setPaymentsTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [savedSellId, setSavedSellId] = useState(null);
  const [balanceUpdate, setBalanceUpdate] = useState(0);
  const {updateTitle, toggleFab, toggleCloseIcon} = useAppBar();
  const { config, isLoadingConfig } = useConfig();

  const dateFormattingLocale = useMemo(() => resolveDateFormattingLocale(config), [config]);
  const useHour12 = useMemo(
    () => resolveHour12Preference(config, dateFormattingLocale),
    [config, dateFormattingLocale]
  );
  const dateTimePickerFormat = useMemo(
    () => getDayjsDateTimeFormatForLocale(dateFormattingLocale, useHour12),
    [dateFormattingLocale, useHour12]
  );
  const dateDisplayFormat = useMemo(
    () => getDayjsDateFormatForLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );
  const dayjsPickerLocale = useMemo(
    () => bcp47ToDayjsLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );

  // Planning dialog states
  const [planningDialogOpen, setPlanningDialogOpen] = useState(false);
  const [planningProduct, setPlanningProduct] = useState(null);
  const [planningDate, setPlanningDate] = useState(dayjs().add(1, 'month'));
  const [planningPet, setPlanningPet] = useState(null);
  const [plannedSupplies, setPlannedSupplies] = useState([]);
  const [planningErrors, setPlanningErrors] = useState({});
  const [planningPets, setPlanningPets] = useState([]);
  const [existingSupplies, setExistingSupplies] = useState([]);

  // Review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  // Usar el store de Zustand
  const {
    sell,
    sellItem,
    paymentItem,
    setSellItem,
    setPaymentItem,
    updateSell,
    addItem,
    removeItem,
    addPayment,
    removePayment,
    resetSell,
    resetSellItem,
    resetPaymentItem
  } = useSellStore();
  
  const { from, state } = useNavigationHelperStore();

  // Referencias para los campos de formulario
  const [errors, setErrors] = useState({});
  const refs = {
    product: useRef(),
    quantity: useRef(),
    price: useRef(),
    discount: useRef(),
    amount: useRef(),
    deposit: useRef(),
    sell_point: useRef(),
    payment: useRef(),
    planningProduct: useRef(),
    planningDate: useRef(),
  };

  // Cargar datos iniciales
  useEffect(() => {

    updateTitle('', t('edit.sellTitle'), t('common.completeData'));
    toggleFab(false);
    toggleCloseIcon(true);

    if (location.state?.pet) {
      updateSell({
        pet: location.state.pet,
        owner: getFirstPrincipalOwner(location.state.pet.owners),
      });
    }

    if (location.state?.owner) {
      updateSell({
        owner: location.state.owner,
        pet: null,
      });
    }

    if (location.state?.product) {
      manageInsertSellItem(location.state.product);
    }

    const fetchData = async () => {
      try {
        const [sellsForInput, products] = await Promise.all([
          fetchForInput("sells", "sells_for_input"),
          fetchProducts()
        ]);
        
        setDeposits(sellsForInput.deposits);
        setSellPoints(sellsForInput.sell_points);
        setPaymentsTypes(sellsForInput.finance_types_payments);
        setProducts(products);

        setTimeout(() => {
          if (refs.product.current && !location.state?.product) {
            refs.product.current.focus();
          }
        }, 100);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Establecer depósito predeterminado
  useEffect(() => {
    setDefaultDeposit();
  }, [deposits]);

  // Establecer punto de venta predeterminado
  useEffect(() => {
    setDefaultSellPoint();    
  }, [sellPoints]);

  const setDefaultDeposit = () => {
    if (deposits.length > 0 && !sell.deposit) {
      updateSell({ deposit: deposits[0] });
    }
  };

  const setDefaultSellPoint = () => {
    if (sellPoints.length > 0 && !sell.sell_point) {
      updateSell({ sell_point: sellPoints[0] });
    }
  };

  // Funciones de búsqueda y remoción de mascota y dueño
  const handleSearchPet = () => {
    navigate("/main/search-pet", { state: { from: "/main/edit-sell" } });
  };
  const handleSearchOwner = () => {
    navigate("/main/search-owner", { state: { from: "/main/edit-sell" } });
  };
  const handleRemovePet = () => {
    updateSell({ pet: null });
  };
  const handleRemoveOwner = () => {
    updateSell({ pet: null, owner: null });
  };

  const handleSearchProduct = () => {
    navigate("/main/search-product", { state: { from: "/main/edit-sell" } });
  };
  
  // Agregar producto: valida campos, agrega el producto y recalcula total y deuda
  const handleAddProduct = () => {
    if (!sellItem.product || !sellItem.quantity || !sellItem.price) {
      snackbar(t('edit.selectProduct'));
      return;
    }

    if (
      !validateDecimal("quantity", sellItem.quantity, setErrors, refs.quantity) ||
      !validateDecimal("price", sellItem.price, setErrors, refs.price) ||
      (sellItem.discount_surcharge &&
        !validateDecimal("discount", sellItem.discount_surcharge, setErrors, refs.discount))
    )
      return;

    addItem(structuredClone(sellItem));
    const { total } = calculateTotalAndDebt([...sell.items, sellItem], sell.payments);
    updateSell({ total });
    resetSellItem();

    refs.product.current.focus();
  };
  // Remover producto y actualizar total y deuda
  const handleRemoveProduct = (index) => {
    removeItem(index);
    const updatedItems = sell.items.filter((_, i) => i !== index);
    const { total } = calculateTotalAndDebt(updatedItems, sell.payments);
    updateSell({ total });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (deposits.length > 1 && !validateNonEmpty("deposit", sell.deposit?.name, setErrors, refs.deposit)) {
      return;
    }

    if (sellPoints.length > 1 && !validateNonEmpty("sell_point", sell.sell_point?.name, setErrors, refs.sell_point)) {
      return;
    }

    if (!sell.items || sell.items.length === 0) {
      snackbar(t('edit.selectProduct'));
      return;
    }

    // Make sure debt is set to total initially
    if (!sell.payments || sell.payments.length === 0) {
      updateSell({ debt: sell.total });
    }

    // Open payment dialog
    setPaymentDialogOpen(true);
    
    // Set default payment method to "Efectivo" if available
    const efectivoPayment = paymentsTypes.find(payment => payment.name.toLowerCase() === "efectivo");
    if (efectivoPayment) {
      setPaymentItem({
        finance_types_payment: efectivoPayment,
        amount: sell.total ? sell.total.toString() : "",
      });
    } else if (paymentsTypes.length > 0) {
      // If "Efectivo" not found, use the first payment method
      const firstPayment = paymentsTypes[0];
      setPaymentItem({
        finance_types_payment: firstPayment,
        amount: sell.total ? sell.total.toString() : "",
      });
    }
    
    // Set focus to amount field after the dialog opens
    setTimeout(() => {
      if (refs.amount.current) {
        refs.amount.current.focus();
        refs.amount.current.select();
      }
    }, 100);
  };

  const save = async () => {
    setIsLoading(true);
    try {
      // Format the date to MySQL datetime format before sending
      const sellToSend = {
        ...sell,
        date: dayjs(sell.date).format('YYYY-MM-DD HH:mm:ss'),
        upcoming_supplies: sell.upcoming_supplies?.map(supply => ({
          ...supply,
          date_tentative: dayjs(supply.date_tentative).format('YYYY-MM-DD') + ' 00:00:00'
        })) || []
      };
      
      const response = await axios.post(`${import.meta.env.VITE_API_URL}sells`, 
        sellToSend, 
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
        }
      });

      resetSell();
      resetPaymentItem();

      // Store the sell ID and show success dialog
      setSavedSellId(response.data.data.id);
      setSuccessDialogOpen(true);

    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  // Calcula el total de la venta y la deuda (total - pagos realizados)
  const calculateTotalAndDebt = (items, payments = []) => {
    let total = new Decimal(0);
    items.forEach((item) => {
      const quantity = new Decimal(item.quantity.toString().replace(",", ".") || 0);
      const price = new Decimal(item.price.toString().replace(",", ".") || 0);
      const discount = new Decimal(item.discount_surcharge.toString().replace(",", ".") || 0);
      const subtotal = quantity.times(price);
      const discountAmount = subtotal.times(discount.div(100));
      total = total.plus(subtotal.minus(discountAmount));
    });
    total = total.toDecimalPlaces(2);

    const totalPayments = payments.reduce(
      (sum, payment) =>
        sum.plus(new Decimal(payment.amount.toString().replace(",", ".") || 0)),
      new Decimal(0)
    );
    const debt = total.minus(totalPayments).toDecimalPlaces(2);
    return { total: total.toNumber(), debt: debt.toNumber() };
  };

  const manageInsertSellItem = (product) => {
    if (product) {
      setSellItem({
        product: product,
        quantity: (1).toFixed(2),
        price: product.p1 ? product.p1.toFixed(2) : (0).toFixed(2),
        discount_surcharge: (0).toFixed(2),
        selected_unit: product.unit.first_unit_string, 
      });

      setTimeout(() => {
        if (refs.quantity.current) {
          refs.quantity.current.focus();
          refs.quantity.current.select();
        }
      }, 100);
    } else {
      resetSellItem();
    }
  };

  // Retorna true si el producto tiene al menos dos precios
  const hasAtLeastTwoPrices = (product) => {
    let count = 0;
    if (product.p1 !== undefined) count++;
    if (product.p2 !== undefined) count++;
    if (product.p3 !== undefined) count++;
    return count >= 2;
  };

  const handleAddPayment = () => {
    if (currentPaymentIsZero()) {
      snackbar(t('edit.enterPaymentAmount'));
      return;
    }

    if (!paymentItem.finance_types_payment) {
      snackbar(t('edit.selectPaymentMethod'));
      return;
    }

    if (!validateDecimal("amount", paymentItem.amount, setErrors, refs.amount))
      return;

    const currentPayment = getCurrentPayment();
    
    if (!isPossiblePayment(currentPayment)) {
      snackbar(t('edit.paymentExceedsTotal'));
      return;
    }

    addPayment(structuredClone(currentPayment));
    const { total, debt } = calculateTotalAndDebt(sell.items, [...sell.payments, currentPayment]);
    updateSell({ total, debt });
  };

  const handleRemovePayment = (index) => {
    removePayment(index);
    const updatedPayments = sell.payments.filter((_, i) => i !== index);
    const { total, debt } = calculateTotalAndDebt(sell.items, updatedPayments);
    updateSell({ total, debt });
  };

  const handlePaymentDialogClose = () => {
    setPaymentDialogOpen(false);
    resetPaymentItem();
  };

  const handlePaymentDialogSave = async () => {
    if (!validateNullOwnerAndTotalPay()) {
      snackbar(t('edit.selectClientForDebt'));
      return;
    }

    let currentPayment = null;
    if (hasNoPayments() && !currentPaymentIsZero()) {
      if (currentPaymentIsGreaterThanTotalToPay()) {
        currentPayment = getCurrentPaymentMax();
      } else {
        currentPayment = getCurrentPayment();
      }
    }

    if (currentPayment) {
      addPayment(structuredClone(currentPayment));
    }

    setPaymentDialogOpen(false);
    
    let displaySellsPlanningActivity = config.vet.sells_planning_activity == 1 && sell.owner != null;
    if (displaySellsPlanningActivity) {
      const planningOwner = await fetchOwner(sell.owner.id, false);
      if (!planningOwner.pets || planningOwner.pets.length === 0) {
        setReviewDialogOpen(true);
        return;
      }

      setPlanningPets(planningOwner.pets);
      setPlanningPet(sell.pet || planningOwner.pets[0]);
      
      // Fetch existing supplies for this sell
      await fetchExistingSupplies(sell);
      
      setPlanningDialogOpen(true);
      
      // Focus on product field when dialog opens
      setTimeout(() => {
        if (refs.planningProduct.current) {
          refs.planningProduct.current.focus();
        }
      }, 100);
    } else {
      setReviewDialogOpen(true);
    }
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    setSavedSellId(null);

    setDefaultDeposit();
    setDefaultSellPoint();
    
    // Handle navigation after closing success dialog
    if (from != "/main" && from && state) {
      navigate(from, state);
    } else {
      if (refs.product.current) {
        setTimeout(() => {
          refs.product.current.focus();
        }, 100);
      }
    }
  };

  const getSumOfPayments = () => {
    return sell.payments.reduce(
      (sum, payment) => sum.plus(new Decimal(payment.amount.toString().replace(",", "."))),
      new Decimal(0)
    );
  };

  const getCurrentPaymentAmount = () => {
    if (!paymentItem?.amount || paymentItem.amount === "") return new Decimal(0);
    return new Decimal(paymentItem.amount.toString().replace(",", "."));
  };

  const getCurrentPayment = () => {
    //console.log("paymentItem", paymentItem);
    return {
      id: paymentItem.finance_types_payment.id,
      name: paymentItem.finance_types_payment.name,
      amount: getCurrentPaymentAmount().toNumber(),
      date: dayjs().format('YYYY-MM-DD'),
    };
  };

  const getCurrentPaymentMax = () => {
    const payment = getCurrentPayment();
    const currentTotal = new Decimal(sell.total);
    const sumOfPayments = getSumOfPayments();
    const remainingAmount = currentTotal.minus(sumOfPayments);
    payment.amount = remainingAmount.toNumber();
    return payment;
  };

  const currentPaymentIsGreaterThanTotalToPay = () => {
    const currentTotal = new Decimal(sell.total);
    const sumOfPayments = getSumOfPayments();
    const totalWithCurrentPayment = sumOfPayments.plus(getCurrentPaymentAmount());
    return totalWithCurrentPayment.greaterThan(currentTotal);
  };

  const currentPaymentIsZero = () => {
    return getCurrentPaymentAmount().equals(0);
  };

  const hasNoPayments = () => {
    return !sell.payments || sell.payments.length === 0;
  };

  const hasDebtBalance = () => {
    const currentTotal = new Decimal(sell.total);
    let sumOfPayments = getSumOfPayments();
    
    if (hasNoPayments()) {
      sumOfPayments = sumOfPayments.plus(getCurrentPaymentAmount());
    }
    
    // console.log("sumOfPayments", sumOfPayments);
    // console.log("currentTotal", currentTotal);

    return sumOfPayments.lessThan(currentTotal);
  };

  const validateNullOwnerAndTotalPay = () => {
    if (!sell.owner && hasDebtBalance()) {
      return false;
    }
    return true;
  };

  const setBalanceInViews = () => {
    const currentPaymentAmount = getCurrentPaymentAmount();
    let sumOfPayments = getSumOfPayments();

    if (hasNoPayments()) {
      sumOfPayments = sumOfPayments.plus(currentPaymentAmount);
    }

    const total = new Decimal(sell.total || 0);
    const balance = sumOfPayments.minus(total);
    const zero = new Decimal(0);

    return {
      balance: balance.toNumber(),
      isDebt: balance.lessThan(zero),
      isChange: balance.greaterThan(zero),
      isPerfect: balance.equals(zero)
    };
  };

  const isPossiblePayment = (payment) => {
    const paymentAmount = new Decimal(payment.amount || 0);
    const currentTotal = new Decimal(sell.total || 0);
    const sumOfPayments = getSumOfPayments();
    const totalWithNewPayment = sumOfPayments.plus(paymentAmount);
    
    return totalWithNewPayment.lessThanOrEqualTo(currentTotal);
  };

  const handleViewReceipt = async () => {
    if (!savedSellId) {
      snackbar(t('edit.sellIdError'));
      return;
    }

    setIsLoading(true);
    try {
      // Make request to backend to get PDF in base64 format
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}sells?pdf_receipt&id=${savedSellId}`,
        { withCredentials: true }
      );

      console.log("response", response);

      if (response.data) {
        // Convert base64 to blob
        const base64Data = response.data;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Create URL and open in new window
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        
        // Clean up URL after a short delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);

        //snackbar('Comprobante abierto correctamente', 'success');
      } else {
        throw new Error(t('edit.invalidResponseFormat'));
      }
    } catch (error) {
      console.error('Error al obtener el comprobante:', error);
      snackbar(t('edit.receiptError'));
    } finally {
      setIsLoading(false);
    }

    setSuccessDialogOpen(false);
    setSavedSellId(null);
  };

  // Update balance when payment amount changes (similar to TextWatcher in Java)
  useEffect(() => {
    if (paymentDialogOpen) {
      setBalanceUpdate(prev => prev + 1);
    }
  }, [paymentItem?.amount, paymentDialogOpen]);

  // Planning dialog functions
  const handleAddSupply = () => {
    if (!planningProduct) {
      snackbar(t('edit.selectProduct'));
      return;
    }

    if (!planningDate) {
      snackbar(t('edit.selectDate'));
      return;
    }

    if (!planningPet) {
      snackbar(t('edit.selectPetForSupply'));
      return;
    }

    // Validate duplicate supply for same date and pet
    const isDuplicateInPlanned = plannedSupplies.some(supply => 
      supply.product.id === planningProduct.id &&
      supply.pet.id === planningPet.id &&
      dayjs(supply.date_tentative).format('YYYY-MM-DD') === planningDate.format('YYYY-MM-DD')
    );

    const isDuplicateInExisting = existingSupplies.some(supply => 
      supply.product.id === planningProduct.id &&
      supply.pet.id === planningPet.id &&
      dayjs(supply.date_tentative).format('YYYY-MM-DD') === planningDate.format('YYYY-MM-DD')
    );

    if (isDuplicateInPlanned || isDuplicateInExisting) {
      snackbar(t('edit.duplicateSupply'));
      return;
    }

    const newSupply = {
      product: planningProduct,
      pet: planningPet,
      date_tentative: planningDate.toDate(),
      planning_activity_new: 1
    };

    setPlannedSupplies(prev => [...prev, newSupply]);
    
    // Reset form
    setPlanningProduct(null);
    //setPlanningDate(dayjs().add(1, 'month'));
    
    // Focus back to product field
    setTimeout(() => {
      if (refs.planningProduct.current) {
        refs.planningProduct.current.focus();
      }
    }, 100);
  };

  const handleRemoveSupply = (index) => {
    setPlannedSupplies(prev => prev.filter((_, i) => i !== index));
  };

  const handlePlanningDialogClose = () => {
    setPlanningDialogOpen(false);
    setPlanningProduct(null);
    setPlanningDate(dayjs().add(1, 'month'));
    setPlanningPet(null);
    setPlanningErrors({});
    setPlanningPets([]);
    setExistingSupplies([]);
    
    // If planning is skipped, go to review dialog
    setReviewDialogOpen(true);
  };

  const handlePlanningDialogSave = () => {
    // Add planned supplies to sell object
    updateSell({ upcoming_supplies: plannedSupplies });
    
    setPlanningDialogOpen(false);
    setReviewDialogOpen(true);
  };

  // Review dialog functions
  const handleReviewDialogClose = () => {
    setReviewDialogOpen(false);
  };

  const handleReviewDialogSave = () => {
    setReviewDialogOpen(false);
    save();
  };

  const getProductsDetails = () => {
    if (!sell.items || sell.items.length === 0) return "";
    
    let productsCount = new Decimal(0);
    let servicesCount = new Decimal(0);

    // Count products and services
    sell.items.forEach((sellItem) => {
      const quantity = new Decimal(sellItem.quantity || 0);
      
      if (sellItem.product.is_service) {
        // It's a service - count the quantity
        servicesCount = servicesCount.plus(quantity);
      } else {
        // It's a product - check the unit
        const selectedUnit = sellItem.selected_unit || sellItem.product.unit.first_unit_string || "";
        
        if (selectedUnit.toLowerCase() === "u" || selectedUnit.toLowerCase() === "pz") {
          // Count by quantity for units and pieces
          productsCount = productsCount.plus(quantity);
        } else {
          // Count as 1 product for other units (L, Kg, etc.)
          productsCount = productsCount.plus(1);
        }
      }
    });

    // Build the details string
    let details = "";
    if (productsCount.greaterThan(0)) {
      details += `${t('common.products')}: ${productsCount.toNumber()} `;
    }
    
    if (servicesCount.greaterThan(0)) {
      details += `${t('common.service')}: ${servicesCount.toNumber()}`;
    }

    return details.trim();
  };

  const calculateBalance = () => {
    const totalPayments = sell.payments?.reduce(
      (sum, payment) => sum.plus(new Decimal(payment.amount || 0)),
      new Decimal(0)
    ) || new Decimal(0);
    
    const total = new Decimal(sell.total || 0);
    return total.minus(totalPayments);
  };

  // Function to fetch existing supplies for planning
  const fetchExistingSupplies = async (sellData) => {
    try {
      setIsLoading(true);

      // Format the date to MySQL datetime format before sending
      const sellToSend = {
        ...sellData,
        date: dayjs(sellData.date).format('YYYY-MM-DD HH:mm:ss')
      };

      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}sells?planning_activity`,
        sellToSend,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      //console.log("response", response);
      setExistingSupplies(response.data.data);
    } catch (error) {
      handleError(error, snackbar);
      setExistingSupplies([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <EditPage
      onSubmit={handleSubmit}
      isLoading={isLoading || isLoadingConfig}
    >
      <Box sx={{ width: "100%", marginTop: 1 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <DateTimePicker
            label={t('common.dateTime')}
            value={sell.date}
            onChange={(newValue) => updateSell({ date: newValue })}
            renderInput={(params) => <TextField {...params} fullWidth />}
            slotProps={{ textField: { fullWidth: true } }}
            format={dateTimePickerFormat}
            ampm={useHour12}
          />
        </LocalizationProvider>
      </Box>

      <Box display="flex" gap={2} alignItems="center">
        <ListItemWAvatarRemove
          primary={sell.pet?.name || t('common.select')}
          onClick={handleSearchPet}
          onDelete={handleRemovePet}
          avatarContent={<PetsIcon />}
        />
        <ListItemWAvatarRemove
          primary={sell.owner?.name || t('common.select')}
          onClick={handleSearchOwner}
          onDelete={handleRemoveOwner}
        />
      </Box>

      {(deposits.length > 1 || sellPoints.length > 1) && (
        <Box
          display="flex"
          gap={2}
          width="100%"
          justifyContent="center"
          sx={{ marginTop: 3 }}
        >
          {deposits.length > 1 && (
            <Box flex={1}>
              <Autocomplete
                options={deposits}
                getOptionLabel={(option) => option.name}
                value={sell.deposit}
                onChange={(_, newValue) => updateSell({ deposit: newValue })}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={t('common.deposit')} 
                    fullWidth 
                    inputRef={refs.deposit}
                    error={!!errors.deposit}
                    helperText={errors.deposit}
                  />
                )}
                freeSolo={false}
              />
            </Box>
          )}
          {sellPoints.length > 1 && (
            <Box flex={1}>
              <Autocomplete
                options={sellPoints}
                getOptionLabel={(option) => option.name}
                value={sell.sell_point}
                onChange={(_, newValue) => updateSell({ sell_point: newValue })}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label={t('edit.sellPoint')} 
                    fullWidth 
                    inputRef={refs.sell_point}
                    error={!!errors.sell_point}
                    helperText={errors.sell_point}
                  />
                )}
                freeSolo={false}
              />
            </Box>
          )}
        </Box>
      )}

      <Divider sx={{ marginY: 2 }} />

      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Typography variant="h6" fontWeight="bold">
          Total: {formatCurrency(sell.total)}
        </Typography>
      </Box>

      <Divider sx={{ marginTop: 2, marginBottom: 1 }} />

      <Box display="flex" gap={1} alignItems="center">
        {/* Product autocomplete with search functionality */}
        <Autocomplete
          fullWidth
          options={products}
          getOptionLabel={(option) => (option ? option.name : "")}
          value={sellItem?.product}
          onChange={(e, newValue) => {
            manageInsertSellItem(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('common.productService')}
              name="product"
              margin="normal"
              inputRef={refs.product}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setTimeout(() => {
                    if (!refs.product.current?.value) {
                      handleSubmit(e);
                    }
                  }, 100);
                }
              }}
            />
          )}
        />

        {/* Product search button */}
        <IconButton onClick={handleSearchProduct} sx={{ marginTop: 1 }}>
          <SearchIcon />
        </IconButton>

        {/* Quantity input field */}
        <TextField
          label={t('common.quantity')}
          type="text"
          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          value={sellItem?.quantity}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setSellItem({ ...sellItem, quantity: value });
          }}
          sx={{ marginTop: 1, "& .MuiInputBase-input": { textAlign: "right" } }}
          inputRef={refs.quantity}
          error={!!errors.quantity}
          helperText={errors.quantity}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              refs.price.current.select();
              refs.price.current.focus();
            }
          }}
        />

        {/* Unit selector - Only displayed for products with complex units */}
        {sellItem.product?.unit.is_complex === 1 && (
          <ProductUnitSelector
            product={sellItem.product}
            sellItem={sellItem}
            setSellItem={setSellItem}
          />
        )}

        {/* Price */}
        <TextField
          label={t('common.price')}
          type="text"
          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          value={sellItem?.price}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setSellItem({ ...sellItem, price: value });
          }}
          sx={{ marginTop: 1, "& .MuiInputBase-input": { textAlign: "right" } }}
          inputRef={refs.price}
          error={!!errors.price}
          helperText={errors.price}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              refs.discount.current.select();
              refs.discount.current.focus();
            }
          }}
          InputLabelProps={{ shrink: !!sellItem?.price }}
        />
        {sellItem.product && hasAtLeastTwoPrices(sellItem.product) && (
          <ProductPriceSelector sellItem={sellItem} setSellItem={setSellItem} />
        )}

        {/* Discount */}  
        <TextField
          label={t('common.discount')}
          type="text"
          inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
          value={sellItem?.discount_surcharge}
          onChange={(e) => {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            setSellItem({ ...sellItem, discount_surcharge: value });
          }}
          sx={{ marginTop: 1, "& .MuiInputBase-input": { textAlign: "right" } }}
          inputRef={refs.discount}
          error={!!errors.discount}
          helperText={errors.discount}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddProduct();
              refs.product.current.select();
              refs.product.current.focus();
            }
          }}
          InputLabelProps={{ shrink: !!sellItem?.discount_surcharge }}
        />

        <IconButton onClick={handleAddProduct} sx={{ marginTop: 1 }}>
          <KeyboardReturnIcon />
        </IconButton>
      </Box>

      {/* Product list */}  
      <Box>
        {sell.items?.map((item, index) => (
          <ListItemProductRemove
            key={index}
            product={item.product.name}
            description={getDescription(item)}
            subtotal={formatCurrency(
              getSubtotal(item.quantity, item.price, item.discount_surcharge)
            )}
            onDelete={handleRemoveProduct}
            index={index}
            thumbUrl={item.product.thumb_url}
            avatarContent={<InventoryIcon sx={{ fontSize: 20 }} />}
          />
        ))}
      </Box>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handlePaymentDialogClose}
        aria-labelledby="payment-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="payment-dialog-title">
          {t('edit.howWillPayment')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText mb={2}>
            Total: {formatCurrency(sell.total)}
            {(() => {
              const balanceInfo = setBalanceInViews();
              
              if (balanceInfo.isPerfect) return null;
              
              if (balanceInfo.isDebt) {
                return (
                  <span style={{ color: 'red', marginLeft: '10px' }}>
                    | {t('edit.debt')} {formatCurrency(Math.abs(balanceInfo.balance))}
                  </span>
                );
              } else if (balanceInfo.isChange) {
                return (
                  <span style={{ color: 'green', marginLeft: '10px' }}>
                    | {t('edit.change')} {formatCurrency(balanceInfo.balance)}
                  </span>
                );
              }
              return null;
            })()}
          </DialogContentText>
          
          {/* Payment input form */}
          <Box display="flex" flexDirection="column" gap={2} mb={2}>
            <TextField
              label={t('common.amount')}
              type="text"
              inputProps={{ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" }}
              value={paymentItem?.amount}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.,]/g, "");
                setPaymentItem({ ...paymentItem, amount: value });
              }}
              sx={{ "& .MuiInputBase-input": { textAlign: "center" } }}
              inputRef={refs.amount}
              error={!!errors.amount}
              helperText={errors.amount}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (refs.payment.current) {
                    refs.payment.current.focus();
                  }
                }
              }}
              fullWidth
            />
            
            <Autocomplete
              fullWidth
              options={paymentsTypes}
              getOptionLabel={(option) => (option ? option.name : "")}
              value={paymentItem?.finance_types_payment}
              disableClearable={true}
              onChange={(e, newValue) => {
                if (newValue) {
                  setPaymentItem({
                    ...paymentItem,
                    finance_types_payment: newValue,
                  });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('edit.paymentMethods')}
                  name="paymentType"
                  inputRef={refs.payment}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handlePaymentDialogSave();
                    }
                  }}
                />
              )}
            />

            <Box display="flex" justifyContent="flex-start">
              <Button 
                onClick={handleAddPayment} 
                variant="text" 
                color="primary"
                sx={{ textTransform: 'none' }}
              >
                {t('edit.addMultiplePayments')}
              </Button>
            </Box>
          </Box>

          {/* Payments list */}
          <List>
            {sell.payments?.map((item, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleRemovePayment(index)}
                    sx={{ width: "42px", height: "42px" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
                sx={{ paddingRight: "56px" }}
              >
                <ListItemText
                  primary={`${dayjs(item.date).format(dateDisplayFormat)}. ${item.name}: ${formatCurrency(item.amount)}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentDialogClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handlePaymentDialogSave} 
            color="primary" 
            autoFocus 
          >
            {t('common.continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Planning Dialog */}
      <Dialog
        open={planningDialogOpen}
        onClose={handlePlanningDialogClose}
        aria-labelledby="planning-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="planning-dialog-title">
          {t('edit.nextSupplyQuestion')}
        </DialogTitle>
        <DialogContent>
          {/* <DialogContentText mb={2}>
            Planifica los productos que se administrarán en futuras visitas
          </DialogContentText> */}
          
          {/* Planning form */}
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            {/* Pet selection - always shown for planning */}
            <Autocomplete
              fullWidth
              freeSolo={false}
              disableClearable
              options={planningPets}
              getOptionLabel={(option) => (option ? option.name : "")}
              value={planningPet}
              onChange={(e, newValue) => setPlanningPet(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={config?.vet?.pet_naming_es || t('edit.petLabel')}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: true,
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddSupply();
                    }
                  }}
                />
              )}
            />

            {/* Product selection and Date in same row */}
            <Box display="flex" gap={2} alignItems="center">
              {/* Date selection - 25% */}
              <Box flex="0.30">
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
                  <DatePicker
                    label={t('edit.tentativeDate')}
                    value={planningDate}
                    onChange={(newValue) => setPlanningDate(newValue)}
                    views={["year", "month", "day"]}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        fullWidth 
                        inputRef={refs.planningDate}
                      />
                    )}
                    slotProps={{ textField: { fullWidth: true } }}
                    format={dateDisplayFormat}
                    minDate={dayjs()}
                  />
                </LocalizationProvider>
              </Box>

              {/* Product selection - 65% */}
              <Box flex="0.70">
                <Autocomplete
                  fullWidth
                  options={products}
                  getOptionLabel={(option) => (option ? option.name : "")}
                  value={planningProduct}
                  onChange={(e, newValue) => setPlanningProduct(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('common.productService')}
                      inputRef={refs.planningProduct}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (planningProduct) {
                            handleAddSupply();
                          }
                        }
                      }}
                    />
                  )}
                />
              </Box>

              {/* Add supply button */}
              <IconButton onClick={handleAddSupply} sx={{ marginTop: 1 }}>
                <KeyboardReturnIcon />
              </IconButton>
            </Box>

            {/* Planned supplies list */}
            {(existingSupplies.length > 0 || plannedSupplies.length > 0) && (
              <>
                <Divider sx={{ mt: 1 }} />
                
                {/* Existing supplies - cannot be deleted */}
                {existingSupplies.map((supply, index) => (
                  <ListItemSupplyRemove
                    key={`existing-${index}`}
                    pet={supply.pet}
                    product={supply.product}
                    dateTentative={supply.date_tentative}
                    info={t('edit.existingSupply')}
                    onDelete={null} // No delete button for existing supplies
                    index={index}
                  />
                ))}
                
                {/* New planned supplies - can be deleted */}
                {plannedSupplies.map((supply, index) => (
                  <ListItemSupplyRemove
                    key={`new-${index}`}
                    pet={supply.pet}
                    product={supply.product}
                    dateTentative={supply.date_tentative}
                    info={t('edit.newSupply')}
                    onDelete={handleRemoveSupply}
                    index={index}
                  />
                ))}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePlanningDialogClose} disabled={isLoading}>
            {t('edit.skip')}
          </Button>
          <Button 
            onClick={handlePlanningDialogSave} 
            color="primary" 
            autoFocus 
            disabled={isLoading}
          >
            {t('common.continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={handleReviewDialogClose}
        aria-labelledby="review-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="review-dialog-title">
          {t('edit.reviewTitle')}
        </DialogTitle>
        <DialogContent>
          {/* <DialogContentText mb={2}>
            Revisa que todo esté bien antes de registrar la venta
          </DialogContentText> */}
          
          <Box display="flex" flexDirection="column" gap={3} mt={0}>
            
            {/* Owner and Pet Info */}
            <Box>
              {sell.pet && (
                <ListItemWAvatarRemove
                  primary={sell.pet.name}
                  secondary={sell.pet.species || ''}
                  avatarContent={<PetsIcon />}
                />
              )}

              {sell.owner && (
                <ListItemWAvatarRemove
                  primary={sell.owner.name}
                  avatarContent={sell.owner.name.charAt(0).toUpperCase()}
                />
              )}
              
            </Box>

            {/* Deposit and Sell Point */}
            {(deposits.length > 1 || sellPoints.length > 1) && (
              <Box>
                {deposits.length > 1 && sell.deposit && (
                  <Typography variant="body2" color="text.secondary">
                    {t('edit.warehouse')} {sell.deposit.name}
                  </Typography>
                )}
                {sellPoints.length > 1 && sell.sell_point && (
                  <Typography variant="body2" color="text.secondary">
                    {t('edit.emits')} {sell.sell_point.name}
                  </Typography>
                )}
              </Box>
            )}

            {/* Products Details */}
            <Box>
              <Typography variant="h6" gutterBottom>
                {t('common.productsServices')}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {getProductsDetails()}
              </Typography>
              <List dense>
                {sell.items?.map((item, index) => (
                  <ListItemProductRemove
                    key={index}
                    product={item.product.name}
                    description={getDescription(item)}
                    subtotal={formatCurrency(
                      getSubtotal(item.quantity, item.price, item.discount_surcharge)
                    )}
                    thumbUrl={item.product.thumb_url}
                    avatarContent={<InventoryIcon sx={{ fontSize: 20 }} />}
                  />
                ))}
              </List>
            </Box>

            {/* Total and Balance */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Total: {formatCurrency(sell.total)}
              </Typography>
              {(() => {
                const balance = calculateBalance();
                const balanceValue = balance.toNumber();
                
                if (balanceValue === 0) {
                  return (
                    <Typography variant="body1" color="success.main">
                      {t('edit.fullPayment')}
                    </Typography>
                  );
                } else if (balanceValue > 0) {
                  return (
                    <Typography variant="body1" color="error.main">
                      {t('edit.debt')} {formatCurrency(balanceValue)}
                    </Typography>
                  );
                } else {
                  return (
                    <Typography variant="body1" color="success.main">
                      {t('edit.change')} {formatCurrency(Math.abs(balanceValue))}
                    </Typography>
                  );
                }
              })()}
            </Box>

            {/* Payments */}
            {sell.payments && sell.payments.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('common.paymentMethod')}
                </Typography>
                <List dense>
                  {sell.payments.map((payment, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemText
                        primary={`${payment.name}: ${formatCurrency(payment.amount)}`}
                        secondary={dayjs(payment.date).format(dateDisplayFormat)}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Planned Supplies */}
            {plannedSupplies.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('edit.nextSuppliesPlanned')}
                </Typography>
                <List dense>
                  {plannedSupplies.map((supply, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemText
                        primary={supply.product.name}
                        secondary={`${supply.pet.name} - ${dayjs(supply.date_tentative).format(dateDisplayFormat)}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReviewDialogClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleReviewDialogSave} 
            color="primary" 
            variant="contained"
            autoFocus 
            disabled={isLoading}
          >
            {t('edit.finalize')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        aria-labelledby="success-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="success-dialog-title" sx={{ textAlign: 'center' }}>
          {t('edit.sellSuccess')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleViewReceipt}
              fullWidth
              size="large"
            >
              {t('edit.viewReceipt')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleSuccessDialogClose}
              fullWidth
              size="large"
            >
              {t('common.close')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

    </EditPage>  );
};

export default EditSell;
